// Libraries
import { Flex, Text } from '@chakra-ui/react';
// Components
import { Wrapper } from './FixedDropdown.wrapper';
// Utils
import text from '@translations/en.json';
// Types
import { BasicOption, FixedDropdownProps, SelectKeys } from './FixedDropdown.type';

/**
 * FixedDropdown component that wraps the Wrapper component and provides fixed dimensions.
 *
 * @component
 * @param {FixedDropdownProps<OptionType, IsMulti>} props - The props for the FixedDropdown component.
 * @param {string} props.w - The width of the FixedDropdown component.
 * @param {Key} props.key- The key used for rendering and identifying the FixedDropdown component.
 * @param {boolean} props.addSelectAll - Indicates whether to add a "Select All" option to the dropdown.
 * @param {string} props.selectAllMessage - The message displayed for the "Select All" option.
 * @param {string} props.selectedLabel -Label that shows the currently selected options.
 * @returns {ReactElement} The FixedDropdown component.
 * @template OptionType - The type of the options in the Wrapper component.
 * @template IsMulti - A boolean value indicating if the Wrapper component allows multiple selections.
 */
export const FixedDropdown = <OptionType extends BasicOption, IsMulti extends boolean>({
  w = 'full',
  key,
  addSelectAll,
  selectAllMessage = text.select__all,
  options,
  selectedLabel,
  ...rest
}: FixedDropdownProps<OptionType, IsMulti>) => {
  const selectAllOption = { value: SelectKeys.SELECT_ALL, label: `${selectAllMessage}` } as OptionType;
  const expandedOptions = addSelectAll ? [selectAllOption, ...options] : options;
  const h = `${44 + expandedOptions.length * 36}px`;

  return (
    <Flex key={key} w={w} direction='column' borderBottom='section'>
      <Flex w='full' p={4} direction='column'>
        <Text fontSize='m' fontWeight='medium'>
          {rest.name}
        </Text>
        <Flex align='center' gap={2}>
          <Text
            fontSize='s'
            overflow='hidden'
            textOverflow='ellipsis'
            color='tealishBlue'
          >{`${text.condition}: ${selectedLabel}`}</Text>
        </Flex>
      </Flex>
      <Flex maxH={62} h={h} direction='column'>
        <Wrapper<OptionType, IsMulti> options={expandedOptions} {...rest} />
      </Flex>
    </Flex>
  );
};
